import { createReducer, on } from '@ngrx/store';

import * as PlanActions from '@core/store/plan/plan.actions';

import { PlanFindingsActions } from './plan-findings.actions';
import { initialPlanFindingsState } from './plan-findings.state';

export const planFindingsReducer = createReducer(
  initialPlanFindingsState,

  on(PlanFindingsActions.contextChanged, (state, { planId, paramId }) => ({
    ...initialPlanFindingsState,
    planId,
    paramId,
    // Carried across the context, unlike everything else: the context is re-announced
    // every time the plan comes back into view, and a check still in flight from before
    // that compares against this counter to tell whether an edit passed under it.
    // Restarting the count would let such a check report itself as current.
    editSeq: state.editSeq
  })),

  // An answer too, if not the server's: nothing is in flight, and the wait must end.
  on(PlanFindingsActions.contextUnavailable, (state, { planId }) => ({
    ...initialPlanFindingsState,
    planId,
    hasResolved: true,
    phase: 'UNAVAILABLE' as const,
    editSeq: state.editSeq
  })),

  on(PlanFindingsActions.readRequested, (state) => ({
    ...state,
    // A running check must stay visible: `read$` reads this phase to suppress the read,
    // and `readSucceeded` uses it to drop a response that overlapped the check. Moving
    // the phase to READING here would take both guards away.
    phase: state.phase === 'CHECKING' ? ('CHECKING' as const) : ('READING' as const)
  })),

  on(PlanFindingsActions.readSucceeded, (state, { response, planId, paramId }) => {
    // A read that outlives its context (the user navigated away before it resolved)
    // would otherwise be applied to whatever plan is now loaded.
    if (state.planId !== planId || state.paramId !== paramId) {
      return state;
    }
    // A running check writes the very row this read fetches, so a read that started
    // before it can come back with the pre-check row and undo it.
    if (state.phase === 'CHECKING') {
      return state;
    }
    return {
      ...state,
      status: response.status,
      findings: response.findings,
      // A read can only ever discover staleness, never disprove a local edit that
      // landed while it was in flight.
      isStale: response.isStale || state.isStale,
      hasResolved: true,
      phase: 'READY' as const
    };
  }),

  on(PlanFindingsActions.checkRequested, (state) => ({
    ...state,
    phase: 'CHECKING' as const
  })),

  on(
    PlanFindingsActions.checkSucceeded,
    (state, { response, planId, paramId, editSeq }) => {
      // A check that outlives its context (the user navigated away before it resolved)
      // would otherwise be applied to whatever plan is now loaded.
      if (state.planId !== planId || state.paramId !== paramId) {
        return state;
      }
      return {
        ...state,
        status: response.status,
        findings: response.findings,
        // The response always reports fresh, but an edit landing mid-check means these
        // findings already describe an earlier state.
        isStale: state.editSeq !== editSeq,
        hasResolved: true,
        phase: 'READY' as const
      };
    }
  ),

  on(PlanFindingsActions.requestFailed, (state, { planId, paramId }) => {
    // A request that outlives its context fails for a plan the user has left; reporting
    // it would put a failure banner on the plan they are now looking at.
    if (state.planId !== planId || state.paramId !== paramId) {
      return state;
    }
    return {
      ...state,
      // A failure is an answer too: it ends the wait, and the panel reports the failure
      // rather than spinning for a response that is never coming.
      hasResolved: true,
      phase: 'FAILED' as const
    };
  }),

  on(PlanFindingsActions.planDataChanged, (state, { planId }) => {
    // No check can describe a plan without a parameter set, so none can go out of date.
    if (state.planId !== planId || state.phase === 'UNAVAILABLE') {
      return state;
    }
    return {
      ...state,
      isStale: true,
      editSeq: state.editSeq + 1
    };
  }),

  // Plan to plan navigation clears no plan, and the next plan's context is only known once its
  // parameter set is: until then, and for good when it has none, the slice would describe the
  // previous plan.
  on(PlanActions.setPlan, (state, { plan }) =>
    state.planId === null || state.planId === plan.id
      ? state
      : { ...initialPlanFindingsState, editSeq: state.editSeq }
  ),

  on(PlanActions.clearPlan, () => initialPlanFindingsState)
);
